import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  createXlsxColumn,
  createXlsxImgColumn,
  createXlsxCellStyle,
  createColumnWidth,
  xlsxCellSerial,
} from "../../service/excel";
import { convertToCanvas } from "../../service/common";

const useExportExcel = ({ isFooter = false, action = {} } = {}) => {
  const setExportColumns = ({ columns }) => {
    if (!columns) return;
  };

  const formatNum = ({ ws, column, cIdx, rIdx, value }) => {
    let numFmt = "#,##0";

    if (column.decimalCount) {
      numFmt += ".";
      for (let i = 0; i < column.decimalCount; i++) numFmt += "0";
    } else if (String(value).includes(".")) {
      numFmt = "#,##0.##";
    }

    if (column.isPercent) numFmt += "%";

    ws.getCell(`${xlsxCellSerial[cIdx]}${rIdx}`).numFmt = numFmt;
  };

  const shouldFormatNumber = ({ value, column }) => {
    return typeof value === "number" && !column.isDate && !column.isString;
  };

  const createList = ({ ws, listColumns, listData, listPos }) => {
    const visibleColumns = listColumns.filter(
      (column) => !column.exportOptions?.isHidden,
    );

    visibleColumns.forEach((column, cIdx) => {
      let {
        formatVal = null,
        bodyColStyles = () => {
          return {};
        },
        footer = null,
      } = column.exportOptions ?? {};

      createXlsxColumn({
        ws,
        column: `${xlsxCellSerial[listPos.x + cIdx]}${listPos.y}`,
        value: column.header,
        styles: createXlsxCellStyle({
          fill_color: "d9d9d9",
        }),
      });

      listData.forEach((item, rIdx) => {
        let value = formatVal
          ? formatVal({
              allData: item,
              data: item[column.key ?? column.field],
              rowIndex: rIdx,
            })
          : item[column.key ?? column.field];

        createXlsxColumn({
          ws,
          column: `${xlsxCellSerial[listPos.x + cIdx]}${
            listPos.y + rIdx + 1
          }`,
          value: value ?? "-",
          styles: createXlsxCellStyle({
            align_h: "left",
            ...bodyColStyles({
              allData: item,
              data: item[column.key ?? column.field],
              field: column.key ?? column.field,
              rowIndex: rIdx,
            }),
          }),
        });

        if (shouldFormatNumber({ value, column })) {
          formatNum({
            ws,
            column,
            cIdx: listPos.x + cIdx,
            rIdx: listPos.y + rIdx + 1,
            value,
          });
        }
      });

      if (isFooter) {
        let value = footer ?? column.footer ?? "";
        createXlsxColumn({
          ws,
          column: `${xlsxCellSerial[listPos.x + cIdx]}${
            listPos.y + listData.length + 1
          }`,
          value: value,
          styles: createXlsxCellStyle({
            fill_color: "d9d9d9",
          }),
        });

        if (typeof value === "number") {
          formatNum({
            ws,
            column,
            cIdx: listPos.x + cIdx,
            rIdx: listPos.y + listData.length + 1,
            value,
          });
        }
      }
    });

    createColumnWidth({
      ws,
      columns: visibleColumns.map((column, cIdx) => ({
        column: xlsxCellSerial[listPos.x + cIdx],
        width:
          column.exportOptions?.width ??
          (column.style?.width ? column.style?.width / 7 : 10),
      })),
    });
  };

  const createImage = async ({ workbook, ws, imgId, imgPos }) => {
    const imgTarget = document.getElementById(imgId);
    const imgResult = await convertToCanvas({ targetId: imgId });

    createXlsxImgColumn({
      workbook,
      ws,
      position: { x: imgPos.x, y: imgPos.y },
      img: imgResult.pngData,
      ext: "png",
      style: {
        width: imgTarget.offsetWidth,
        height: imgTarget.offsetHeight,
      },
    });
  };

  const exportExcel = async ({
    workbook = new ExcelJS.Workbook(),
    workbookName,
    sheetOptions = [],
  } = {}) => {
    for (const sheetOption of sheetOptions) {
      let {
        ws = null,
        isList = true,
        listOptions = [],
        isImg = false,
        imgOptions = [],
        otherWorksheetAction = null,
      } = sheetOption;

      if (!ws) ws = workbook.addWorksheet("詳細資料");

      if (isList) {
        listOptions.forEach((listOp) => {
          const {
            listColumns = [],
            listData = [],
            listPos = { x: 0, y: 1 },
          } = listOp;

          createList({
            ws,
            listColumns,
            listData,
            listPos,
          });
        });
      }

      if (isImg) {
        for (const imgOption of imgOptions) {
          const { imgId = null, imgPos = { x: 0, y: 1 } } = imgOption;
          await createImage({
            workbook,
            ws,
            imgId,
            imgPos,
          });
        }
      }

      if (otherWorksheetAction) otherWorksheetAction({ ws });
    }

    const mainBuffer = await workbook.xlsx.writeBuffer();
    const mainFileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const mainBlob = new Blob([mainBuffer], { type: mainFileType });
    saveAs(mainBlob, `${workbookName}.xlsx`);
    action.finish();
  };

  return { setExportColumns, exportExcel };
};

export default useExportExcel;
